import { computed, inject, untracked } from '@angular/core';
import { patchState, signalStore, withComputed, withFeature, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap } from 'rxjs';

import { TenantsResponse } from '@core/api/tenant/tenant.models';
import { TenantService } from '@core/api/tenant/tenant.service';

import { getAccessibleSites, mapTenantsResponseApiModelToStoreModel } from '@domain/tenant/tenant-models.mappings';
import { TenantDtoStoreModel } from '@domain/tenant/tenant.models';

import { withApiResource } from '@shared/stores/features/with-api-resource.feature';
import { withCancellation } from '@shared/stores/features/with-cancellation.feature';

interface TenantsState {
  tenants: TenantDtoStoreModel[] | undefined;
}

const initialState: TenantsState = {
  tenants: undefined,
};

export const TenantsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCancellation('clear'),
  withFeature((store) => {
    const tenantService = inject(TenantService);
    return withApiResource('tenants', () => tenantService.getTenants$(), store.clear$);
  }),
  withComputed(({ tenants, tenantsIsLoading, tenantsError }) => {
    const sites = computed(() => getAccessibleSites(tenants()));

    return {
      sites,
      isLoading: computed(() => tenantsIsLoading()),
      error: computed(() => tenantsError()),
      singleSite: computed(() => (sites()?.length === 1 ? sites()![0] : undefined)),
      siteTimeZones: computed(() => new Map<string, string>(sites()?.map((site) => [site.id, site.timeZone]))),
      hasResolved: computed(() => sites() !== undefined || tenantsError() !== undefined),
      isInitialLoading: computed(() => sites() === undefined && tenantsIsLoading()),
      anySiteHasTelemetryFeaturesEnabled: computed(
        () => sites()?.some((site) => site.features?.telemetryFeaturesEnabled === true) ?? false
      ),
    };
  }),
  withMethods((store) => {
    const applyLoadedTenants = rxMethod<TenantsResponse>(
      pipe(tap((response) => patchState(store, { tenants: mapTenantsResponseApiModelToStoreModel(response).tenants })))
    );
    applyLoadedTenants(store.tenantsLoadSuccess$);

    return {
      ensureTenantsLoaded(): void {
        const tenantsAreLoadedOrLoading = untracked(() => store.tenants() !== undefined || store.isLoading());
        if (!tenantsAreLoadedOrLoading) {
          store.tenantsLoad(undefined);
        }
      },

      clearTenants(): void {
        store.clear();
        patchState(store, initialState);
      },
    };
  })
);

export type TenantsStoreInstance = InstanceType<typeof TenantsStore>;
